import React, { useState, useRef, useEffect } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { Loader2, Plus, Paperclip, X, Calendar as CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar as CalendarPicker } from '@/components/ui/calendar'
import { projectService } from '@/services/projectService'
import { taskService } from '@/services/taskService'
import { globalTaskService } from '@/services/globalTaskService'
import RichTextEditor from './RichTextEditor'
import ProjectForm from './ProjectForm'
import TaskTypeIcon from './TaskTypeIcon'
import TaskPicker from './TaskPicker'
import LabelsInput from './LabelsInput'
import { cn } from '@/lib/utils'

const TASK_TYPE_KEY = 'last-task-type-id'

const CreateTaskDialog = ({ open, onOpenChange, defaultProjectId }) => {
  const queryClient = useQueryClient()
  const fileInputRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [projectFormOpen, setProjectFormOpen] = useState(false)
  const [files, setFiles] = useState([])
  const [form, setForm] = useState({
    project_id: '',
    title: '',
    assignee_id: '',
    priority: 'medium',
    status_id: '',
    description: '',
    type_id: parseInt(localStorage.getItem(TASK_TYPE_KEY)) || 1,
    parent: null, // { id, title, task_number, type }
    labels: [],
    due_date: '', // yyyy-MM-dd
  })

  // Auto-populate project when defaultProjectId changes or dialog opens
  useEffect(() => {
    if (open && defaultProjectId) {
      setForm(f => ({ ...f, project_id: String(defaultProjectId) }))
    }
  }, [open, defaultProjectId])

  const { data: projectsData } = useQuery(
    ['projects', { status: 'active' }],
    () => projectService.getProjects({ status: 'active' }),
    { enabled: open }
  )

  const { data: usersData } = useQuery(
    'team-members',
    () => globalTaskService.getTeamMembers(),
    { enabled: open }
  )

  // Fetch statuses when project is selected
  const { data: statusData } = useQuery(
    ['projects', form.project_id, 'statuses'],
    () => projectService.getStatuses(form.project_id),
    { enabled: open && !!form.project_id }
  )

  const { data: typesData } = useQuery(
    'task-types',
    () => taskService.getTaskTypes(),
    { staleTime: 60000 * 5 }
  )

  const projects = projectsData?.data?.projects || []
  const users = usersData?.data?.users || []
  const statuses = statusData?.data?.statuses || []
  const taskTypes = typesData?.data?.types || []

  // Epic (level 1) → Task/Bug/Story (0) → Sub-task (-1): a parent sits one level above
  const selectedType = taskTypes.find(t => t.id === form.type_id)
  const selectedLevel = selectedType?.hierarchy_level ?? 0
  const selectedProject = projects.find(p => String(p.id) === form.project_id)

  const mentionUsers = React.useMemo(() =>
    users.map(u => ({
      id: String(u.id),
      label: u.employee?.first_name
        ? `${u.employee.first_name} ${u.employee.last_name}`
        : u.email,
    })),
    [users]
  )

  const reset = () => {
    setForm({
      project_id: defaultProjectId ? String(defaultProjectId) : '',
      title: '',
      assignee_id: '',
      priority: 'medium',
      status_id: '',
      description: '',
      type_id: form.type_id,
      parent: null,
      labels: [],
      due_date: '',
    })
    setFiles([])
  }

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || [])
    setFiles(prev => [...prev, ...selected])
    e.target.value = ''
  }

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.project_id || !form.title.trim()) return
    if (selectedLevel === -1 && !form.parent) return toast.error('Choose the parent task for this sub-task')

    setLoading(true)
    try {
      const data = {
        title: form.title.trim(),
        priority: form.priority,
        type_id: form.type_id,
        ...(form.assignee_id ? { assignee_id: parseInt(form.assignee_id) } : {}),
        ...(form.status_id ? { status_id: parseInt(form.status_id) } : {}),
        ...(form.description && form.description !== '<p></p>' ? { description: form.description } : {}),
        ...(form.parent ? { parent_id: form.parent.id } : {}),
        ...(form.labels.length ? { labels: form.labels } : {}),
        ...(form.due_date ? { due_date: form.due_date } : {}),
      }
      localStorage.setItem(TASK_TYPE_KEY, String(form.type_id))
      const result = await taskService.createTask(form.project_id, data)
      const newTaskId = result?.data?.task?.id

      // Upload attachments if any
      if (files.length > 0 && newTaskId) {
        try {
          await taskService.uploadAttachments(form.project_id, newTaskId, files)
        } catch {
          toast.error('Task created but some attachments failed to upload')
        }
      }

      toast.success('Task created')
      queryClient.invalidateQueries(['projects', form.project_id, 'tasks'])
      queryClient.invalidateQueries(['my-tasks'])
      reset()
      onOpenChange(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create task')
    } finally {
      setLoading(false)
    }
  }

  const getUserName = (user) => {
    if (user.employee?.first_name) {
      return `${user.employee.first_name} ${user.employee.last_name || ''}`.trim()
    }
    return user.email
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v) }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 -mx-6">
            <form id="create-task-form" onSubmit={handleSubmit} className="space-y-4 pb-2 px-6 py-1">
              {/* Project */}
              <div className="space-y-2">
                <Label>Project *</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Select value={form.project_id} onValueChange={(v) => setForm(f => ({ ...f, project_id: v, status_id: '', parent: null }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded shrink-0" style={{ backgroundColor: p.color || '#3B82F6' }} />
                              {p.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setProjectFormOpen(true)}
                    title="Create new project"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Type + Title */}
              <div className="space-y-2">
                <Label>Type & Title *</Label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center border rounded-md overflow-hidden shrink-0">
                    {taskTypes.map((t) => {
                      const isSelected = form.type_id === t.id
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, type_id: t.id, parent: f.parent && (f.parent.type?.hierarchy_level ?? 0) === (t.hierarchy_level ?? 0) + 1 ? f.parent : null }))}
                          className={cn(
                            'flex items-center gap-1 px-2 py-1.5 text-xs font-medium transition-colors',
                            isSelected ? 'bg-muted' : 'hover:bg-muted/50'
                          )}
                          title={t.name}
                        >
                          <TaskTypeIcon type={t} />
                          {t.name}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Input
                  id="title"
                  placeholder="Task title"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  autoFocus
                />
              </div>

              {/* Epic / parent + labels */}
              <div className="grid grid-cols-2 gap-3">
                {selectedLevel < 1 ? (
                  <div className="space-y-2">
                    <Label>{selectedLevel === -1 ? 'Parent task *' : 'Epic'}</Label>
                    <TaskPicker
                      projectId={form.project_id || undefined}
                      projectKey={selectedProject?.key}
                      filter={(t) => (t.type?.hierarchy_level ?? 0) === selectedLevel + 1}
                      onSelect={(t) => setForm(f => ({ ...f, parent: t }))}
                    >
                      <Button type="button" variant="outline" className="w-full justify-start font-normal" disabled={!form.project_id}>
                        {form.parent ? (
                          <span className="flex items-center gap-1.5 truncate">
                            <TaskTypeIcon type={form.parent.type} />
                            <span className="font-mono text-[11px] text-muted-foreground">{selectedProject?.key}-{form.parent.task_number}</span>
                            <span className="truncate">{form.parent.title}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">{selectedLevel === -1 ? 'Choose parent task' : 'None'}</span>
                        )}
                      </Button>
                    </TaskPicker>
                  </div>
                ) : <div />}
                <div className="space-y-2">
                  <Label>Labels</Label>
                  <LabelsInput
                    value={form.labels}
                    onChange={(labels) => setForm(f => ({ ...f, labels }))}
                    projectId={form.project_id || undefined}
                    className="min-h-10"
                  />
                </div>
              </div>

              {/* Status + Assignee + Priority + Due date */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={form.status_id}
                    onValueChange={(v) => setForm(f => ({ ...f, status_id: v }))}
                    disabled={!form.project_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Default" />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                              s.category === 'done' ? 'bg-green-500' :
                              s.category === 'in_progress' ? 'bg-blue-500' : 'bg-slate-400'
                            }`} />
                            {s.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Assignee</Label>
                  <Select value={form.assignee_id} onValueChange={(v) => setForm(f => ({ ...f, assignee_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {getUserName(u)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-start font-normal px-3">
                        <CalendarIcon className="h-3.5 w-3.5 mr-1.5 shrink-0 text-muted-foreground" />
                        {form.due_date
                          ? <span className="truncate">{format(new Date(form.due_date + 'T00:00:00'), 'MMM d, yyyy')}</span>
                          : <span className="text-muted-foreground">None</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarPicker
                        mode="single"
                        selected={form.due_date ? new Date(form.due_date + 'T00:00:00') : undefined}
                        onSelect={(date) => setForm(f => ({ ...f, due_date: date ? format(date, 'yyyy-MM-dd') : '' }))}
                        initialFocus
                      />
                      {form.due_date && (
                        <div className="p-2 border-t">
                          <Button type="button" variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setForm(f => ({ ...f, due_date: '' }))}>
                            Clear date
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Description (Rich Text) */}
              <div className="space-y-2">
                <Label>Description</Label>
                <RichTextEditor
                  content={form.description}
                  onChange={(html) => setForm(f => ({ ...f, description: html }))}
                  placeholder="Task description..."
                  minHeight={120}
                  mentionUsers={mentionUsers}
                />
              </div>

              {/* Attachments */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Attachments</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="h-3.5 w-3.5 mr-1" />
                    Attach files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
                {files.length > 0 && (
                  <div className="space-y-1.5">
                    {files.map((file, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-sm bg-muted/50 rounded-md px-3 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">{formatFileSize(file.size)}</span>
                        </div>
                        <button type="button" onClick={() => removeFile(i)} className="text-muted-foreground hover:text-foreground shrink-0">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>
          </ScrollArea>

          <DialogFooter className="pt-2 border-t">
            <Button type="button" variant="outline" onClick={() => { reset(); onOpenChange(false) }}>
              Cancel
            </Button>
            <Button form="create-task-form" type="submit" disabled={!form.project_id || !form.title.trim() || loading}>
              {loading && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline Project Creation */}
      <ProjectForm
        open={projectFormOpen}
        onOpenChange={(v) => {
          setProjectFormOpen(v)
          if (!v) queryClient.invalidateQueries(['projects', { status: 'active' }])
        }}
        users={users}
      />
    </>
  )
}

export default CreateTaskDialog
